import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@env/environment';
import { MonoTypeOperatorFunction, Observable, catchError, map, throwError } from 'rxjs';
import {
    EnrollmentDetail,
    EnrollmentDetailsResponse,
    QualificationResponse,
} from '../../interfaces/enrollment-detail.interface';

export interface CertificateData {
    pngUrl: string;
    recipientInitials: string;
    recipientName: string;
    recipientProfilePhotoUrl: string;
    dateOfIssue: string;
    courseName: string;
    facultyName: string;
    courseStartDate: string;
    courseEndDate: string;
}

export interface EnrollmentCertificatePdfResponse {
    data: string;
    totalRecords: number;
    errors: unknown;
    hasNext: boolean;
}

@Injectable({
    providedIn: 'root',
})
export class EnrollmentDetailsService {
    private readonly http = inject(HttpClient);
    private readonly baseUrl = environment.API_URL;

    /**
     * Fetches enrollment details from the API.
     * @param enrollmentId - The enrollment ID to fetch details for
     * @param isParent - Flag indicating if the request is for parent view
     * @returns Observable of EnrollmentDetail
     */
    getEnrollmentDetails(enrollmentId: string, isParent: boolean): Observable<EnrollmentDetail> {
        const params = new HttpParams().set('enrollmentId', enrollmentId).set('isParent', String(isParent));

        return this.http.get<EnrollmentDetailsResponse>(`${this.baseUrl}/students/enrollment/details`, { params }).pipe(
            map((response) => this.assertValidEnrollmentResponse(response)),
            this.handleHttpError('Failed to load enrollment details'),
        );
    }

    getCertificatePath(
        enrollmentId: string,
        force: boolean = false,
    ): Observable<{ data: string; errors: string | null }> {
        return this.http
            .get<{
                data: string;
                errors: string | null;
            }>(`${this.baseUrl}/certificate/student/png/${enrollmentId}`, { params: { force: force.toString() } })
            .pipe(this.handleHttpError('Failed to fetch certificate path'));
    }

    getCertificateAsBlob(encodedFilePath: string): Observable<Blob> {
        return this.http
            .get(`${this.baseUrl}/file-system/${encodeURIComponent(encodedFilePath)}`, {
                responseType: 'blob',
            })
            .pipe(this.handleHttpError('Failed to fetch certificate blob'));
    }

    getQualifications(page: number = 1, size: number = 50): Observable<QualificationResponse> {
        const params = new HttpParams().set('page', String(page)).set('size', String(size));

        return this.http
            .get<QualificationResponse>(`${this.baseUrl}/qualification`, { params })
            .pipe(this.handleHttpError('Failed to load qualifications'));
    }

    getEnrollmentCertificatePdftoDownload(enrollmentId: string): Observable<EnrollmentCertificatePdfResponse> {
        return this.http
            .get<EnrollmentCertificatePdfResponse>(`${this.baseUrl}/certificate/student/pdf/${enrollmentId}`)
            .pipe(this.handleHttpError('Failed to fetch certificate PDF path'));
    }

    getEncryptedEnrollmentId(enrollmentId: string): Observable<string> {
        return this.http
            .get(`${this.baseUrl}/certificate/generate/${enrollmentId}`, { responseType: 'text' })
            .pipe(this.handleHttpError('Failed to generate encrypted enrollment id'));
    }

    getCertificatePublicUrl(enrollmentId: string): Observable<{ data: CertificateData }> {
        return this.http
            .get<{ data: CertificateData }>(`${this.baseUrl}/certificate/${enrollmentId}`)
            .pipe(this.handleHttpError('Failed to load certificate public URL'));
    }

    /**
     * @deprecated Mock enrollment data has been removed. The observable always errors.
     */
    getMockEnrollmentDetailsByStatus(_status: string): Observable<EnrollmentDetail> {
        return throwError(
            () =>
                new Error(
                    'Mock enrollment details are no longer supported. Use getEnrollmentDetails with a real enrollment id.',
                ),
        );
    }

    /**
     * @deprecated Mock data is no longer used.
     */
    isMockDataEnabled(): boolean {
        return false;
    }

    private handleHttpError<T>(fallbackMessage: string): MonoTypeOperatorFunction<T> {
        return catchError((error: unknown) => this.propagateError(error, fallbackMessage));
    }

    private assertValidEnrollmentResponse(response: EnrollmentDetailsResponse): EnrollmentDetail {
        if (!response?.enrollmentCourse) {
            throw new Error('Invalid enrollment details response from server.');
        }
        return response;
    }

    private propagateError(error: unknown, fallbackMessage: string): Observable<never> {
        if (error instanceof HttpErrorResponse) {
            const message = this.resolveHttpErrorMessage(error, fallbackMessage);
            console.error(fallbackMessage, error);
            return throwError(() => new Error(message));
        }
        if (error instanceof Error) {
            console.error(fallbackMessage, error);
            return throwError(() => error);
        }
        console.error(fallbackMessage, error);
        return throwError(() => new Error(fallbackMessage));
    }

    private resolveHttpErrorMessage(error: HttpErrorResponse, fallback: string): string {
        if (error.error instanceof ErrorEvent) {
            return error.error.message || 'A network error occurred';
        }
        const body = error.error;
        if (body && typeof body === 'object' && 'message' in body) {
            const msg = (body as { message: unknown }).message;
            if (typeof msg === 'string' && msg.length > 0) {
                return msg;
            }
        }
        if (typeof body === 'string' && body.trim().length > 0) {
            return body;
        }
        if (error.status === 0) {
            return 'Unable to reach the server. Check your connection and try again.';
        }
        return error.message || fallback;
    }
}
